// src/pages/AddCourse.jsx
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import Api from "../../service/http";
import {
    validateCourseDocumentFiles,
    validateCourseUploadsForSubmit,
    validateCourseVideoFile,
} from "../../utils/courseFileLimits";

const PLACEHOLDER_IMG = "https://via.placeholder.com/300x180?text=No+Image";
const PLACEHOLDER_VIDEO = "https://via.placeholder.com/300x180?text=No+Video";

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
    ],
};

const AddCourse = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        name: "",
        description: "",
        details: "",
    });

    const [imageFile, setImageFile] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState("");
    const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

    const [newDocuments, setNewDocuments] = useState([]);
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const documentInputRef = useRef(null);

    useEffect(() => {
        return () => {
            if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
            if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
        };
    }, [imagePreviewUrl, videoPreviewUrl]);

    const onImageClick = () => imageInputRef.current?.click();
    const onVideoClick = () => videoInputRef.current?.click();
    const onDocumentClick = () => documentInputRef.current?.click();

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
        setImageFile(file);
        setImagePreviewUrl(URL.createObjectURL(file));
    };

    const handleVideoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const err = validateCourseVideoFile(file);
        if (err) {
            alert(err);
            e.target.value = "";
            return;
        }
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
        setVideoFile(file);
        setVideoPreviewUrl(URL.createObjectURL(file));
    };

    const handleDocumentChange = (e) => {
        const files = Array.from(e.target.files);
        const { valid, errors } = validateCourseDocumentFiles(files);
        if (errors.length) {
            alert(errors.join("\n"));
        }
        if (valid.length) {
            setNewDocuments((prev) => [...prev, ...valid]);
        }
        e.target.value = "";
    };

    const handleDeleteNewDoc = (index) => {
        setNewDocuments((prev) => prev.filter((_, i) => i !== index));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const uploadErr = validateCourseUploadsForSubmit(videoFile, newDocuments);
        if (uploadErr) {
            alert(uploadErr);
            return;
        }

        setLoading(true);

        const fd = new FormData();
        fd.append("name", form.name);
        fd.append("description", form.description ?? "");
        fd.append("details", form.details ?? "");
        if (imageFile) {
            fd.append("image", imageFile);
        }
        if (videoFile) {
            fd.append("video", videoFile);
        }
        newDocuments.forEach((doc) => {
            fd.append("documents[]", doc.file);
        });

        try {
            const res = await Api.post("web/course", fd);
            alert(res.data?.message);
            if (res.status === 200) {
                navigate("/courses/edit/" + res.data?.data?.id);
            }
        } catch (err) {
            console.error("Add course failed", err);
            alert("Failed to add course.");
        }
        setLoading(false);
    };

    return (
        <div className="p-0 h-full overflow-y-auto scrollbar-hide bg-gray-50/50">
            <div className="max-w-7xl mx-auto p-4 sm:p-6">
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Add New Course</h1>
                            <p className="text-gray-500 text-sm mt-1">Create a new course with video, image and documents.</p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => navigate("/courses")}
                                className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all text-sm shadow-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-8 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all text-sm shadow-md shadow-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? "Saving..." : "Add Course"}
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-6 sm:p-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                            <div>
                                <label className="block mb-2 text-sm font-semibold text-gray-700">Featured Image</label>
                                <div
                                    className="w-full h-64 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer overflow-hidden flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors group"
                                    onClick={onImageClick}
                                    title="Click to upload image"
                                >
                                    <img
                                        src={imagePreviewUrl || PLACEHOLDER_IMG}
                                        alt="Course"
                                        className="object-contain w-full h-full group-hover:scale-105 transition-transform duration-300"
                                    />
                                </div>
                                <input
                                    type="file"
                                    name="image"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    ref={imageInputRef}
                                    className="hidden"
                                />
                            </div>

                            <div>
                                <label className="block mb-2 text-sm font-semibold text-gray-700">Featured Video</label>
                                <p className="text-xs text-gray-500 mb-2">Maximum file size: 10 MB</p>
                                <div
                                    className="w-full h-64 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer overflow-hidden flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors group"
                                    onClick={onVideoClick}
                                    title="Click to upload video"
                                >
                                    {videoPreviewUrl ? (
                                        <video src={videoPreviewUrl} controls className="object-contain w-full h-full" />
                                    ) : (
                                        <div className="text-center">
                                            <img
                                                src={PLACEHOLDER_VIDEO}
                                                alt="No video"
                                                className="object-contain w-48 h-32 mx-auto mb-2 opacity-50 group-hover:scale-105 transition-transform duration-300"
                                            />
                                            <p className="text-sm text-gray-400">Click to upload video</p>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    name="video"
                                    accept="video/*"
                                    onChange={handleVideoChange}
                                    ref={videoInputRef}
                                    className="hidden"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2 space-y-6">
                                <div>
                                    <label className="block mb-1.5 text-sm font-semibold text-gray-700">Course Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="e.g. Advanced Options Trading"
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block mb-1.5 text-sm font-semibold text-gray-700">Description</label>
                                    <textarea
                                        name="description"
                                        value={form.description}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm h-32 resize-none"
                                        placeholder="Briefly describe what this course is about..."
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block mb-1.5 text-sm font-semibold text-gray-700">Details (Rich Text)</label>
                                    <div className="prose-sm">
                                        <ReactQuill
                                            theme="snow"
                                            value={form.details}
                                            onChange={(content) =>
                                                setForm((prev) => ({ ...prev, details: content }))
                                            }
                                            modules={quillModules}
                                            className="mb-4 rounded-xl overflow-hidden border border-gray-200"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100 h-fit">
                                <label className="block mb-1 text-sm font-semibold text-gray-700">Course Documents</label>
                                <p className="text-xs text-gray-500 mb-4">PDF only · maximum 2 MB per file</p>

                                <div className="space-y-3 mb-6">
                                    {newDocuments.length > 0 ? (
                                        newDocuments.map((doc, index) => (
                                            <div key={`${doc.name}-${index}`} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-sm">
                                                <span className="truncate flex-1 mr-2 text-gray-600">{index + 1}. {doc?.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteNewDoc(index)}
                                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Remove"
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                                            <p className="text-xs text-gray-400">No documents uploaded</p>
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={onDocumentClick}
                                    className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-50 transition-all shadow-sm"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Upload Document(s)
                                </button>
                                <input
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    multiple
                                    ref={documentInputRef}
                                    onChange={handleDocumentChange}
                                    className="hidden"
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddCourse;
